import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarView, calendarViews, dayKey, parseDayKey, startOfDay } from './calendarDates';
import {
  CalendarFilterValues,
  hasActiveFilters,
  parseCalendarFilters,
  writeCalendarFilter,
} from './calendarFilterModel';

function isCalendarView(value: string | null): value is CalendarView {
  return value !== null && (calendarViews as readonly string[]).includes(value);
}

/**
 * The view, the date, the filters and the open post of the Calendar, kept in the address (design
 * a1, a3: `?view=&date=&songs=&channels=&states=&post=`). So a push or an email link can open this
 * exact view. The old `postId` of the push and email links opens the post too.
 *
 * With no `view`, the narrow screen shows the List and the wide screen the Month.
 */
export function useCalendarFilters(isNarrow: boolean) {
  const [params, setParams] = useSearchParams();

  const view: CalendarView = useMemo(() => {
    const value = params.get('view');
    if (isCalendarView(value)) {
      return value;
    }
    return isNarrow ? 'list' : 'month';
  }, [isNarrow, params]);

  const dateParam = params.get('date');
  const anchor = useMemo(() => parseDayKey(dateParam) ?? startOfDay(new Date()), [dateParam]);
  const filters: CalendarFilterValues = useMemo(() => parseCalendarFilters(params), [params]);
  const postUuid = params.get('post') ?? params.get('postId');

  const update = useCallback(
    (change: (next: URLSearchParams) => void) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          change(next);
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const setView = useCallback((next: CalendarView) => update((query) => query.set('view', next)), [update]);
  const setAnchor = useCallback((next: Date) => update((query) => query.set('date', dayKey(next))), [update]);
  const setFilter = useCallback(
    (key: keyof CalendarFilterValues, values: string[]) => update((query) => writeCalendarFilter(query, key, values)),
    [update]
  );
  const clearFilters = useCallback(
    () =>
      update((query) => {
        query.delete('songs');
        query.delete('channels');
        query.delete('states');
      }),
    [update]
  );
  /** Opens the day panel on a post, or closes it with null. */
  const openPost = useCallback(
    (uuid: string | null) =>
      update((query) => {
        query.delete('postId');
        if (uuid === null) {
          query.delete('post');
        } else {
          query.set('post', uuid);
        }
      }),
    [update]
  );

  return {
    view,
    anchor,
    filters,
    isFiltered: hasActiveFilters(filters),
    postUuid,
    setView,
    setAnchor,
    setFilter,
    clearFilters,
    openPost,
  };
}

export type CalendarFiltersState = ReturnType<typeof useCalendarFilters>;
